/* Copyright (c) 2024-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

export interface RestoreFocusOptions {
    disableFocusRestore?: boolean;
    /**
     * Specifies the element onto which the focus should be restored when a modal is closed.
     * If not specified, `document.activeElement` will be used.
     */
    restoreFocusTarget?: HTMLElement;
}

export interface NvHigIconTextDescription {
    matIcon?: string;
    imageUrl?: string;
    text: string;
    placeholders?: object;
    translated?: boolean;
}

export interface NvHigText {
    text: string;
    semanticHeadingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
    placeholders?: object;
    translated?: boolean;
}

export interface NvHigTableColumns {
    key: string;
    title: string;
    cells: NvHigTableCell[];
    cta?: NvDialogButton;
}

export enum NvHigTableColumnKey {
    SingleSession = 'SingleSession',
    Persistent = 'Persistent'
}

export interface NvHigTableCell {
    iconName?: string;
    description: NvHigText;
    warning?: boolean;
}

export interface NvHigCellComparison {
    primaryText: string;
    secondaryText: string;
}

export interface NvListItem {
    label: string;
    divider?: boolean;
    selected?: boolean;
    /** Any component which renders a `NvListItem` may need to implement the disabled state by
     * manually overriding the CSS if not provided by default (e.g. by Angular components).
     * For some components, this may not be required (e.g. a Rotary does not function well with
     * disabled items).
     */
    disabled?: boolean;
    [k: string]: any;
}

export interface NvHigButtonLink {
    url?: string;
    shortUrl?: string;
    shortUrlDescription?: string;
    routerLink?: {
        commands: any[] | string;
        skipLocationChange?: boolean;
    };
    linkClickedCallback?: () => void;
}

export interface NvDialogLink {
    text: string;
    placeholders?: object;
    url?: string;
    shortUrl?: string;
    shortUrlDescription?: string;
    routerLink?: {
        commands: any[] | string;
        skipLocationChange?: boolean;
    };
    linkClickedCallback?: () => void;
    isTextWithHyperlinkTag?: boolean;
    // Translation key for aria-label that accepts {{context}} placeholder (e.g., 'common.externalLinkAriaLabel' which includes "Opens in new window")
    translatedAriaLabel?: string; // Use pre-translated  to avoid continuous translate pipe computations during change detection
}

export interface NvDialogImage {
    url: string;
    shortcutUrl?: string;
    caption?: string;
    captionPlaceholder?: object;
    description?: string;
    isDraggableUrl?: boolean;
    dragStartCallback?: () => void;
}

export interface NvDialogButton<T = string> {
    showStringInsteadOfCTA?: boolean;
    text: string;
    accentColor?: boolean;
    isDisabled?: boolean;
    spinnerOverlay?: boolean;
    removeButtonTextOnClick?: boolean;
    id?: T;
    hasNotificationBadge?: boolean;
    ariaLabel?: string;
    /** Optional trailing SVG icon (e.g. 'open_in_new' for external links) */
    svgIcon?: string;
}

export interface NvDialogCheckbox {
    text: string;
    initialState: boolean;
    primaryActionInThreeQuarterSheet?: boolean;
    ariaLabel?: string;
}

export interface NvHigTranslateValues {
    [k: string]: string;
}

export interface NvDialogDataType {
    showSpinner: boolean;
}

export interface NvHigRadioButtonOption {
    value: string;
    /** When true, skips translation of value (already translated) */
    translated?: boolean;
    imageUrl?: string;
    ariaLabel?: string;
    imageAlt?: string;
    sectionHeader?: string;
    statusText?: string;
    /** When true, shows a green checkmark badge on the option (e.g., selected platform) */
    showCheckmark?: boolean;
}

export type NvHigRadioButtonOptions = NvHigRadioButtonOption[];
